import {
  type ReorderModalLocationAddressQuery,
  type ReorderModalLocationRestaurantQuery,
  useReorderModalLocationAddressQuery,
  useReorderModalLocationRestaurantQuery,
} from '../../graphql/ReorderModalLocation.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the order channel and name of the location that the reorder modal
 * adds items to, named the same way as in the menu header.
 */
export const useReorderModalLocation = (params: Params) => {
  const { restaurantId, addressId } = params;

  const [{ data: addressData }] = useReorderModalLocationAddressQuery({
    variables: { addressId: addressId ?? '' },
    requestPolicy: 'cache-first',
    pause: !addressId,
  });

  const [{ data: restaurantData }] = useReorderModalLocationRestaurantQuery({
    variables: { id: restaurantId ?? '' },
    requestPolicy: 'cache-first',
    pause: !restaurantId || Boolean(addressId),
  });

  return getReorderModalLocation({
    addressId,
    address: addressData?.address ?? undefined,
    restaurant: restaurantData?.restaurant ?? undefined,
  });
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getReorderModalLocation(
  params: GetReorderModalLocationParams,
): ReorderModalLocation | undefined {
  const { addressId, address, restaurant } = params;

  const isDelivery = Boolean(addressId);

  if (isDelivery && address) {
    return { orderChannel: 'delivery', locationName: address.name ?? address.street };
  }

  if (isDelivery || !restaurant) {
    return;
  }

  return {
    orderChannel: restaurant.isOutpost ? 'outpost' : 'pickup',
    locationName: restaurant.name,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantId?: number;
  addressId?: string;
};

type GetReorderModalLocationParams = {
  addressId?: string;
  address?: NonNullable<ReorderModalLocationAddressQuery['address']>;
  restaurant?: NonNullable<ReorderModalLocationRestaurantQuery['restaurant']>;
};

type ReorderModalLocation = {
  orderChannel: 'pickup' | 'outpost' | 'delivery';
  locationName: string;
};
